import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = [
    'container',
    'list',
    'fileRow',
    'input',
    'dropContainer',
    'dropFieldImage',
    'uploadingImage',
    'uploadedImage',
    'descriptionContainer',
    'uploadedFilename',
    'category',
    'errorMessageContainer',
    'filterCategory',
    'filterUploaderName',
  ];

  static values = {
    url: String,
  };

  connect() {
    this.addDropEvents();
    this.updateAttachmentsCount();
    this.dragCounter = 0;
  }

  clickUploadButtonHandler() {
    this.inputTarget.click();
  }

  inputHandler() {
    const { files } = this.inputTarget;
    if (!files || files.length < 1) {
      this.displayDragAndDropField();
      return;
    }

    this.errorMessageContainerTarget.classList.add('hidden');

    const formData = new FormData();
    for (let i = 0; i < files.length; i += 1) {
      formData.append('images[]', files[i]);
    }
    const category = this.categoryTarget.value;
    if (!category) {
      this.showErrorMessage('カテゴリーを入力してください');
      this.displayDragAndDropField();
      return;
    }

    formData.append('category', category);
    this.showUploadingMask();

    fetch(this.urlValue, {
      method: 'POST',
      headers: {
        'X-CSRF-TOKEN': document.querySelector('[name=csrf-token]')?.content,
      },
      body: formData,
    }).then((response) => {
      if (!response.ok) {
        this.inputTarget.value = null;
        return response.json().then((error) => {
          throw new Error(error.error);
        });
      }

      response.json().then((data) => {
        if (files.length === 1) {
          this.uploadedFilenameTarget.innerHTML = files[0].name;
        } else {
          this.uploadedFilenameTarget.innerHTML = `${files.length}個のファイル`;
        }

        this.showUploadedMask();
        this.inputTarget.value = null;
        const uploadedFilesTemplate = document.createElement('template');
        uploadedFilesTemplate.innerHTML = data.uploaded_files_html.trim();
        this.listTarget.appendChild(uploadedFilesTemplate.content);

        document.querySelector('.form-infos').innerHTML = data.form_infos_html.trim();
        this.updateAttachmentsCount();
        setTimeout(() => { this.hideMaskField(); }, 2000);
      });
      return true;
    }).catch((error) => {
      this.showErrorMessage(error.message);
      this.hideMaskField();
    });
  }

  sort({ params: { sortBy } }) {
    if (!['filename', 'uploaderName', 'createdAt', 'category', 'size'].includes(sortBy)) {
      return;
    }

    const sortTargets = this.fileRowTargets;
    let sortDirection;
    if (this.listTarget.dataset.sortedBy === sortBy
      && this.listTarget.dataset.sortDirection === 'asc') {
      sortDirection = 'desc';
    } else {
      sortDirection = 'asc';
    }

    sortTargets.sort((targetA, targetB) => {
      let weightA = targetA.dataset[sortBy];
      let weightB = targetB.dataset[sortBy];

      if (sortBy === 'size') {
        weightA = Number(weightA);
        weightB = Number(weightB);
      }

      if (weightA === weightB) {
        return 0;
      } if (weightA > weightB) {
        return (sortDirection === 'asc' ? 1 : -1);
      }
      return (sortDirection === 'asc' ? -1 : 1);
    });

    this.listTarget.inner_html = '';
    sortTargets.forEach((e) => { this.listTarget.appendChild(e); });
    this.listTarget.dataset.sortedBy = sortBy;
    this.listTarget.dataset.sortDirection = sortDirection;
  }

  displayDragAndDropField() {
    if (this.dragCounter > 0) {
      this.dropContainerTarget.classList.remove('hidden');
      this.dropFieldImageTarget.classList.remove('hidden');
      this.descriptionContainerTarget.classList.add('hidden');
    } else {
      this.dropContainerTarget.classList.add('hidden');
      this.dropFieldImageTarget.classList.add('hidden');
      this.uploadingImageTarget.classList.add('hidden');
      this.uploadedImageTarget.classList.add('hidden');
      this.descriptionContainerTarget.classList.remove('hidden');
    }
  }

  addDropEvents() {
    const container = this.containerTarget;

    container.addEventListener('dragenter', (e) => {
      e.preventDefault();
      this.dragCounter += 1;
      this.displayDragAndDropField();
    });

    container.addEventListener('dragover', (e) => {
      e.preventDefault();
    });

    container.addEventListener('dragleave', () => {
      this.dragCounter -= 1;
      this.displayDragAndDropField();
    });

    container.addEventListener('drop', (e) => {
      e.preventDefault();

      this.dragCounter = 0;
      const { files } = e.dataTransfer;
      if (!files || files.length === 0) {
        this.displayDragAndDropField();
        return;
      }

      this.inputTarget.files = files;
      this.inputTarget.dispatchEvent(new Event('input'));
    });
  }

  showUploadingMask() {
    this.descriptionContainerTarget.classList.add('hidden');
    this.dropContainerTarget.classList.remove('hidden');
    this.dropFieldImageTarget.classList.add('hidden');
    this.uploadingImageTarget.classList.remove('hidden');
  }

  showUploadedMask() {
    this.descriptionContainerTarget.classList.add('hidden');
    this.dropContainerTarget.classList.remove('hidden');
    this.dropFieldImageTarget.classList.add('hidden');
    this.uploadingImageTarget.classList.add('hidden');
    this.uploadedImageTarget.classList.remove('hidden');
  }

  hideMaskField() {
    this.dropContainerTarget.classList.add('hidden');
    this.dropFieldImageTarget.classList.add('hidden');
    this.uploadingImageTarget.classList.add('hidden');
    this.uploadedImageTarget.classList.add('hidden');
    this.descriptionContainerTarget.classList.remove('hidden');
  }

  showErrorMessage(message) {
    this.errorMessageContainerTarget.classList.remove('hidden');
    this.errorMessageContainerTarget.querySelector('.title').innerHTML = message;
    this.inputTarget.value = null;
  }

  filterList() {
    const category = this.filterCategoryTarget.value;
    const uploaderName = this.filterUploaderNameTarget.value;
    this.fileRowTargets.forEach((fileRow) => {
      if ((!uploaderName && !category)
        || (!category && fileRow.dataset.uploaderName === uploaderName)
        || (fileRow.dataset.category === category && !uploaderName)
        || (fileRow.dataset.category === category && fileRow.dataset.uploaderName === uploaderName)
      ) {
        fileRow.classList.remove('hidden');
      } else {
        fileRow.classList.add('hidden');
      }
    });
  }

  updateAttachmentsCount() {
    document.querySelectorAll('.attachments-count').forEach((counter) => {
      counter.innerHTML = this.fileRowTargets.length;
    });
  }
}
